/**
 * Everything in a snapshot that is not a player.
 *
 * ## Two populations, drawn two different ways
 *
 * **Monsters are characters**, so they go through `render/crowd.ts` with the players: a slot
 * id and sixteen floats in a texture, no `Object3D`, no `SkinnedMesh`, no mixer. A Chorister
 * and a Waker cost the same because they are instances of the same mesh.
 *
 * **Everything else is furniture** and keeps the primitives it always had: a loot bag is a
 * sack, a Choir Anchor is a pillar with a light shaft, the Verger is a cargo frame with an
 * organ in its chest, and the five deployables are the little machines the squad put there.
 * None of those is a person and none of them animates a skeleton; they are a handful of boxes
 * with one moving part each, and there are never many of them on screen at once.
 *
 * ## The bodies the crowd actually has
 *
 * The bake ships two: `human` and `hollow` (`CROWD_BODIES` in `render/crowd.ts`). There is no
 * Chorister mesh, no Cantor mesh and no Sexton mesh, so **every monster wears `hollow`** and
 * is told apart by its tint and its scale — ash for a Chorister, a colder grey for a Cantor
 * that washes to white while it winds up, and a bigger, steel-plated Sexton. When the bake
 * grows real bodies this is the one table that moves (`CROWD_KIND`).
 *
 * ## What went with the rigs
 *
 * `hymnrigs.ts` and its whole budget — `MAX_ANIMATED_HYMNS`, `MAX_SHADOW_HYMNS`, the
 * hysteresis band, the LOD mixer clock — existed because six skinned casters were the most
 * expensive thing in the frame. Instanced bodies cast no shadow map at all (blob shadows,
 * `docs/PERF-BUDGET.md` rule 2) and have no mixer to step, so there is no budget left to keep.
 *
 * The per-monster `Sprite` health bar went too: a bar was two sprites and two draw calls, and
 * an MMO reads health off the target frame in the HUD. The furniture keeps its bar — the
 * Choir Anchor's is the only sign that destroying it is working.
 */
import {
  AdditiveBlending,
  BackSide,
  BoxGeometry,
  Color,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshBasicNodeMaterial,
  MeshStandardNodeMaterial,
  Object3D,
  PlaneGeometry,
  Sprite,
  SpriteMaterial,
  TorusGeometry,
  Vector3,
  type Scene,
} from 'three/webgpu';
import {
  AnimState,
  ENTITY_MAX_HP,
  EntityKind,
  EntityState,
  INTERP_DELAY_MS,
  Race,
  clamp,
  lerp,
  lerpAngle,
  packLook,
} from '@stillsun/shared';
import type { EntitySnap, HitTarget, Vec3 } from '@stillsun/shared';
import type { CrowdField } from '../render/crowd.js';
import { releaseSlot } from './remote.js';
import { sound, type LoopHandle } from '../audio/sound.js';

const RING = 24;
const MAX_EXTRAPOLATE_MS = 100;
/** No snapshot for this long means the server dropped the entity; reap it. */
const STALE_MS = 3000;
/** How long the health bar stays up after the last hp change. */
const DAMAGE_BAR_MS = 3000;
/** Server ids at or above this are entities, not players (see server room.ts). */
const ENTITY_ID_BASE = 40_000;
/** Seconds a corpse takes to sink out of sight. */
const SINK_SECONDS = 1;
const SINK_DEPTH = 2.5;

export const ENTITY_NAMES: Record<number, string> = {
  [EntityKind.Chorister]: 'Chorister',
  [EntityKind.Cantor]: 'Cantor',
  [EntityKind.Sexton]: 'Sexton',
  [EntityKind.Verger]: 'The Verger',
  [EntityKind.ChoirAnchor]: 'Choir Anchor',
  [EntityKind.LootBag]: 'Loot Bag',
  [EntityKind.Bulwark]: 'Bulwark',
  [EntityKind.Sentry]: 'Sentry',
  [EntityKind.Drone]: 'Mend Drone',
  [EntityKind.Beacon]: 'Beacon',
  [EntityKind.Fabricator]: 'Fabricator',
};

/**
 * Kinds the click ray passes straight through. A loot bag is scenery; a Mend Drone hovers
 * 1.5 m above the position the snapshot reports, so a feet-anchored capsule would block a ray
 * in mid-air under it.
 */
const NON_TARGET_KINDS = new Set<number>([EntityKind.LootBag, EntityKind.Drone]);

/** What a reveal effect paints: Hymns, never the squad's own deployables. */
const HYMN_KINDS = new Set<number>([
  EntityKind.Chorister,
  EntityKind.Cantor,
  EntityKind.Sexton,
  EntityKind.Verger,
]);

/**
 * The kinds that keep a primitive body. Everything else is a person and goes on the crowd —
 * including a kind the protocol grows later, which draws as a plain Hollow rather than as
 * nothing at all.
 */
const PRIMITIVE_KINDS = new Set<number>([
  EntityKind.Verger,
  EntityKind.ChoirAnchor,
  EntityKind.LootBag,
  EntityKind.Bulwark,
  EntityKind.Sentry,
  EntityKind.Drone,
  EntityKind.Beacon,
  EntityKind.Fabricator,
]);

export function isEntityId(id: number): boolean {
  return id >= ENTITY_ID_BASE;
}

export interface NearEntity {
  id: number;
  kind: number;
  dist: number;
  pos: Vec3;
}

/** An entity currently swinging something: where it is and which way it is facing. */
export interface Attacker {
  id: number;
  kind: number;
  pos: Vec3;
  yaw: number;
  targetId: number;
}

interface Sample {
  t: number; // server time, ms
  pos: Vec3;
  yaw: number;
  hp: number;
  state: number;
}

const AMBER = new Color(0xffa040);
const WHITE = new Color(0xfff4e6);

/** Reused for every `sound.*` call in this file; the audio layer reads it and never keeps it. */
const SCRATCH: Vec3 = { x: 0, y: 0, z: 0 };

// --- monsters on the crowd ---------------------------------------------------------------

/** How a monster kind is dressed, scaled and coloured on the shared Hollow body. */
interface CrowdKindDef {
  /** Packed `look`. Every monster is a Hollow; gear ids the bake lacks simply draw nothing. */
  look: number;
  /** Uniform body scale. The one lever that separates a Sexton from a Chorister. */
  scale: number;
  /** Base tint, multiplied into the body colour by `crowd`'s material. */
  tint: readonly [number, number, number];
  /** Capsule height for the click ray, in metres. */
  height: number;
}

const HOLLOW = packLook({ race: Race.Hollow });

/**
 * Ash first, colour second: a Hymn is a silhouette, and the only thing allowed to be bright is
 * a Cantor mid-wind-up (`chargeT` below washes its tint to white over the two-second tell).
 */
const CROWD_KIND: Record<number, CrowdKindDef> = {
  [EntityKind.Chorister]: { look: HOLLOW, scale: 1.12, tint: [0.24, 0.22, 0.2], height: 2.1 },
  [EntityKind.Cantor]: { look: HOLLOW, scale: 1.04, tint: [0.26, 0.28, 0.34], height: 2.0 },
  [EntityKind.Sexton]: { look: HOLLOW, scale: 1.42, tint: [0.34, 0.36, 0.38], height: 2.4 },
};
const CROWD_FALLBACK: CrowdKindDef = {
  look: HOLLOW,
  scale: 1,
  tint: [0.3, 0.3, 0.3],
  height: 2,
};

function crowdKind(kind: number): CrowdKindDef {
  return CROWD_KIND[kind] ?? CROWD_FALLBACK;
}

/** `EntityState` -> the clip the body plays. `AnimState` is the crowd's whole vocabulary. */
function animForState(state: number, speed: number): number {
  switch (state) {
    case EntityState.Dead:
      return AnimState.Dead;
    case EntityState.Attack:
      return AnimState.Attack;
    case EntityState.Charging:
      return AnimState.Cast;
    case EntityState.Stagger:
      return AnimState.Hit;
    default:
      // Patrol, Alert and Idle are all "is it moving?" — the server does not say walk or run,
      // and the rendered ground speed is a truer answer than a state id would be.
      return speed < SPEED_EPSILON ? AnimState.Idle : speed > 3.2 ? AnimState.Run : AnimState.Walk;
  }
}

/** Ground speed below which a body is coasting on interpolation noise, not moving. */
const SPEED_EPSILON = 0.05;
/** A tab that was backgrounded returns one enormous `dt`; never read a speed out of it. */
const SPEED_MAX = 20;
/** Metres per second one walk cycle covers. Above this the clip clock scales up. */
const WALK_CYCLE_SPEED = 2.6;

/** Height of a Choir Anchor's beam emitter above its feet. */
const ANCHOR_TOP_Y = 2.6;
/** Point on the Verger the anchor beams feed — its organ chest, not its feet. */
const VERGER_CHEST_Y = 3.05;
const BEAM_UP = new Vector3(0, 1, 0);
const BEAM_DIR = new Vector3();
const BOSS_POS = new Vector3();

/**
 * How far a damage bar is worth drawing. Two `Sprite`s per damaged thing is two draw calls
 * apiece, and a 0.9 m bar past 60 m is a couple of pixels tall under the fog — invisible, but
 * still a full transparent pass.
 *
 * Written by `EntityManager.update()` once per frame from the viewer main.ts hands in, and
 * read by every `animate()` in the same pass.
 */
const HEALTH_BAR_RANGE_M = 60;
const HEALTH_BAR_RANGE_SQ = HEALTH_BAR_RANGE_M * HEALTH_BAR_RANGE_M;
const VIEWER: Vec3 = { x: 0, y: 0, z: 0 };
let viewerKnown = false;

// --- shared primitives ------------------------------------------------------
// Every piece of furniture of a kind is identical, so geometry and (almost) every material is
// a module-level singleton built on first use. The exceptions are called out where they are
// cloned.

interface Geos {
  slitWide: BoxGeometry;
  bag: IcosahedronGeometry;
  bulwark: BoxGeometry;
  bulwarkRib: BoxGeometry;
  tripodLeg: BoxGeometry;
  sentryHead: BoxGeometry;
  barrel: CylinderGeometry;
  droneShell: IcosahedronGeometry;
  droneLamp: IcosahedronGeometry;
  pole: CylinderGeometry;
  ring: TorusGeometry;
  crate: BoxGeometry;
  holo: PlaneGeometry;
  pillar: BoxGeometry;
  pillarBand: BoxGeometry;
  beam: CylinderGeometry;
  vergerTorso: BoxGeometry;
  vergerLeg: BoxGeometry;
  vergerArm: BoxGeometry;
  pipe: CylinderGeometry;
}
let geos: Geos | null = null;
function geometries(): Geos {
  geos ??= {
    slitWide: new BoxGeometry(0.5, 0.06, 0.06),
    bag: new IcosahedronGeometry(0.26, 0),
    // Bulwark: a 3 m half-height hull plate, ribbed so it reads as salvage, not a wall.
    bulwark: new BoxGeometry(3, 1.1, 0.16),
    bulwarkRib: new BoxGeometry(0.12, 1.24, 0.28),
    // Sentry: tripod + barrel.
    tripodLeg: new BoxGeometry(0.07, 0.95, 0.07),
    sentryHead: new BoxGeometry(0.34, 0.3, 0.42),
    barrel: new CylinderGeometry(0.055, 0.055, 0.62, 8),
    // Mend Drone: a small hovering lantern.
    droneShell: new IcosahedronGeometry(0.18, 0),
    droneLamp: new IcosahedronGeometry(0.1, 0),
    // Beacon: pole with a pulsing ring.
    pole: new CylinderGeometry(0.05, 0.07, 1.5, 8),
    ring: new TorusGeometry(0.36, 0.035, 6, 24),
    // Fabricator: a crate with a holo panel standing off it.
    crate: new BoxGeometry(0.9, 0.62, 0.68),
    holo: new PlaneGeometry(0.72, 0.44),
    // Choir Anchor: a pillar with a light shaft up to the Verger.
    pillar: new BoxGeometry(0.52, 2.6, 0.52),
    pillarBand: new BoxGeometry(0.6, 0.09, 0.6),
    beam: new CylinderGeometry(0.05, 0.05, 1, 6, 1, true),
    // The Verger: a choir wearing a cargo frame.
    vergerTorso: new BoxGeometry(2.1, 2.3, 1.35),
    vergerLeg: new BoxGeometry(0.5, 1.9, 0.5),
    vergerArm: new BoxGeometry(0.34, 2.1, 0.34),
    pipe: new CylinderGeometry(0.1, 0.1, 1, 8),
  };
  return geos;
}

interface Mats {
  body: MeshStandardNodeMaterial;
  slit: MeshStandardNodeMaterial;
  bag: MeshStandardNodeMaterial;
  shell: MeshStandardNodeMaterial;
  barBg: SpriteMaterial;
  barFill: SpriteMaterial;
  /** Deployables are Waker-made: hull grey with amber accents, never Hymn black. */
  gear: MeshStandardNodeMaterial;
  gearGlow: MeshStandardNodeMaterial;
  /** Unlit additive: beacon rings, holo panels, the anchor's light shaft. */
  glow: MeshBasicNodeMaterial;
  hymnGlow: MeshBasicNodeMaterial;
}
let mats: Mats | null = null;
function materials(): Mats {
  mats ??= {
    // Ash black. Hymn furniture is silhouette first; only the slit is allowed to glow.
    body: new MeshStandardNodeMaterial({ color: 0x14120f, roughness: 0.85, metalness: 0.05 }),
    slit: new MeshStandardNodeMaterial({
      color: 0x2a1b10,
      emissive: AMBER.clone(),
      emissiveIntensity: 5,
      roughness: 0.4,
      metalness: 0,
    }),
    bag: new MeshStandardNodeMaterial({
      color: 0x1a1512,
      emissive: AMBER.clone(),
      emissiveIntensity: 3,
      roughness: 0.8,
      metalness: 0,
    }),
    // One shell material for every entity: only one is ever visible at a time, so a
    // single animated emissiveIntensity drives the whole highlight pulse.
    shell: new MeshStandardNodeMaterial({
      color: 0x000000,
      emissive: AMBER.clone(),
      emissiveIntensity: 3,
      side: BackSide,
      depthWrite: false,
      roughness: 1,
      metalness: 0,
    }),
    // Flat colour sprites, not canvas textures: the bar only ever animates transforms.
    barBg: new SpriteMaterial({ color: 0x0b0908, depthWrite: false, sizeAttenuation: true }),
    barFill: new SpriteMaterial({ color: 0xffa040, depthWrite: false, sizeAttenuation: true }),
    gear: new MeshStandardNodeMaterial({ color: 0x4e5257, roughness: 0.6, metalness: 0.5 }),
    gearGlow: new MeshStandardNodeMaterial({
      color: 0x2a1b10,
      emissive: AMBER.clone(),
      emissiveIntensity: 4,
      roughness: 0.4,
      metalness: 0,
    }),
    glow: new MeshBasicNodeMaterial({
      color: AMBER.clone(),
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
      blending: AdditiveBlending,
    }),
    hymnGlow: new MeshBasicNodeMaterial({
      color: WHITE.clone(),
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      blending: AdditiveBlending,
    }),
  };
  return mats;
}

interface Silhouette {
  root: Object3D;
  /** World-space height of the built shape, for placing the health bar and the hit capsule. */
  height: number;
  /** Non-null only when this instance owns a cloned slit material (the Verger). */
  ownSlit: MeshStandardNodeMaterial | null;
  /** Choir Anchor only: the light shaft, re-aimed at the Verger every frame. */
  beam: Mesh | null;
  /** Parts that spin/bob/pulse on their own (sentry head, drone lamp, beacon ring). */
  spinner: Object3D | null;
  /** Bar width over this shape; the Verger's is wide because the Verger is. */
  barWidth: number;
}

function buildSilhouette(kind: number): Silhouette {
  const g = geometries();
  const m = materials();
  const root = new Object3D();

  if (kind === EntityKind.Bulwark) {
    const plate = new Mesh(g.bulwark, m.gear);
    plate.position.y = 0.55;
    plate.castShadow = true;
    plate.receiveShadow = true;
    root.add(plate);
    for (const x of [-1.1, 0, 1.1]) {
      const rib = new Mesh(g.bulwarkRib, m.body);
      rib.position.set(x, 0.62, 0);
      root.add(rib);
    }
    // A single amber stripe along the top edge: the only thing you can see over cover.
    const lip = new Mesh(g.pillarBand, m.gearGlow);
    lip.scale.set(4.8, 0.4, 0.28);
    lip.position.y = 1.12;
    root.add(lip);
    return { root, height: 1.15, ownSlit: null, beam: null, spinner: null, barWidth: 0.9 };
  }

  if (kind === EntityKind.Sentry) {
    for (const a of [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3]) {
      const leg = new Mesh(g.tripodLeg, m.gear);
      leg.position.set(Math.sin(a) * 0.22, 0.46, Math.cos(a) * 0.22);
      leg.rotation.z = Math.sin(a) * 0.32;
      leg.rotation.x = -Math.cos(a) * 0.32;
      leg.castShadow = true;
      root.add(leg);
    }
    const head = new Object3D();
    head.position.y = 1.02;
    const box = new Mesh(g.sentryHead, m.gear);
    box.castShadow = true;
    head.add(box);
    const barrel = new Mesh(g.barrel, m.body);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 0, -0.48); // yaw 0 faces -Z, same as the sim
    head.add(barrel);
    const eye = new Mesh(g.pillarBand, m.gearGlow);
    eye.scale.set(0.42, 0.5, 0.1);
    eye.position.set(0, 0.06, -0.22);
    head.add(eye);
    root.add(head);
    return { root, height: 1.3, ownSlit: null, beam: null, spinner: head, barWidth: 0.9 };
  }

  if (kind === EntityKind.Drone) {
    const hover = new Object3D();
    hover.position.y = 1.5;
    const shell = new Mesh(g.droneShell, m.gear);
    shell.castShadow = true;
    hover.add(shell);
    const lamp = new Mesh(g.droneLamp, m.gearGlow);
    lamp.position.y = -0.2;
    hover.add(lamp);
    root.add(hover);
    return { root, height: 1.8, ownSlit: null, beam: null, spinner: hover, barWidth: 0.9 };
  }

  if (kind === EntityKind.Beacon) {
    const pole = new Mesh(g.pole, m.gear);
    pole.position.y = 0.75;
    pole.castShadow = true;
    root.add(pole);
    const ring = new Mesh(g.ring, m.glow);
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 1.5;
    ring.renderOrder = 6;
    root.add(ring);
    const lamp = new Mesh(g.droneLamp, m.gearGlow);
    lamp.position.y = 1.5;
    root.add(lamp);
    return { root, height: 1.9, ownSlit: null, beam: null, spinner: ring, barWidth: 0.9 };
  }

  if (kind === EntityKind.Fabricator) {
    const crate = new Mesh(g.crate, m.gear);
    crate.position.y = 0.31;
    crate.castShadow = true;
    crate.receiveShadow = true;
    root.add(crate);
    const holo = new Mesh(g.holo, m.glow);
    holo.position.set(0, 0.95, 0);
    holo.renderOrder = 6;
    root.add(holo);
    const strip = new Mesh(g.pillarBand, m.gearGlow);
    strip.scale.set(1.4, 0.3, 1.06);
    strip.position.y = 0.6;
    root.add(strip);
    return { root, height: 1.2, ownSlit: null, beam: null, spinner: holo, barWidth: 0.9 };
  }

  if (kind === EntityKind.ChoirAnchor) {
    const pillar = new Mesh(g.pillar, m.body);
    pillar.position.y = 1.3;
    pillar.castShadow = true;
    root.add(pillar);
    for (const y of [0.7, 1.5, 2.3]) {
      const band = new Mesh(g.pillarBand, m.slit);
      band.position.y = y;
      root.add(band);
    }
    // The tether to the Verger. Unit-length along +Y; `aimBeam` scales and points it.
    // Deliberately *not* added to `root`: the manager parents it to the unrotated group
    // so the aim maths never has to undo the anchor's yaw (and the outline shell, which
    // clones `root`, never gets a ghost copy of it).
    const beam = new Mesh(g.beam, m.hymnGlow);
    beam.position.y = ANCHOR_TOP_Y;
    beam.renderOrder = 7;
    beam.visible = false;
    return { root, height: 2.7, ownSlit: null, beam, spinner: null, barWidth: 0.9 };
  }

  if (kind === EntityKind.Verger) {
    // A cargo frame with an organ built into the chest. Everything is black except the
    // pipes, which take the charge ramp.
    for (const x of [-0.72, 0.72]) {
      const leg = new Mesh(g.vergerLeg, m.body);
      leg.position.set(x, 0.95, 0);
      leg.castShadow = true;
      root.add(leg);
    }
    const torso = new Mesh(g.vergerTorso, m.body);
    torso.position.y = 3.05;
    torso.castShadow = true;
    root.add(torso);
    for (const x of [-1.28, 1.28]) {
      const arm = new Mesh(g.vergerArm, m.body);
      arm.position.set(x, 2.9, 0.1);
      arm.castShadow = true;
      root.add(arm);
    }
    const ownSlit = m.slit.clone();
    // Seven pipes of rising length across the chest — the organ, and the charge tell.
    const pipes = [0.9, 1.2, 1.5, 1.75, 1.5, 1.2, 0.9];
    pipes.forEach((len, i) => {
      const pipe = new Mesh(g.pipe, ownSlit);
      pipe.scale.set(1, len, 1);
      pipe.position.set(-0.72 + i * 0.24, 2.9 + len / 2 - 0.6, -0.72);
      root.add(pipe);
    });
    const brow = new Mesh(g.slitWide, ownSlit);
    brow.scale.set(2.6, 1.4, 1.4);
    brow.position.set(0, 4.1, -0.7);
    root.add(brow);
    return { root, height: 4.6, ownSlit, beam: null, spinner: null, barWidth: 2.2 };
  }

  // Loot bag, and anything else that asked for furniture.
  const sack = new Mesh(g.bag, m.bag);
  sack.position.y = 0.2;
  sack.scale.set(1.0, 0.72, 1.0); // squashed, so it reads as a sack and not a rock
  sack.castShadow = true;
  root.add(sack);
  return { root, height: 0.45, ownSlit: null, beam: null, spinner: null, barWidth: 0.9 };
}

class RenderedEntity {
  readonly id: number;
  readonly kind: number;
  /** True when this thing is a person and lives in the crowd rather than in the scene graph. */
  private readonly onCrowd: boolean;
  private readonly crowd: CrowdField | null;
  private slot = -1;
  private readonly def: CrowdKindDef;
  /** Last tint written to the crowd, so a still Chorister costs no texture write. */
  private tintR = -1;
  private tintG = -1;
  private tintB = -1;
  private named = false;

  // --- furniture only. `null` for a crowd body, and nothing below is then built. ---
  /** The scene node, for a primitive body. Crowd bodies have no `Object3D` of any kind. */
  readonly group: Group | null;
  /** Yaw and the death sink live here so the billboard bar never orbits the entity. */
  private readonly pivot: Object3D | null;
  private readonly shell: Object3D | null;
  private readonly barBg: Sprite | null;
  private readonly barFill: Sprite | null;
  private readonly ownSlit: MeshStandardNodeMaterial | null;
  private readonly beam: Mesh | null;
  private readonly spinner: Object3D | null;
  private readonly barWidth: number;
  private readonly barY: number;

  /** Capsule height for the click ray. */
  readonly height: number;

  private readonly samples: Sample[] = [];

  lastSeen = 0;
  private hp = 0;
  private maxHpSeen: number;
  private level = 0;
  private hpChangedAt = -Infinity;
  private state: number = EntityState.Idle;
  /** Milliseconds left on this entity's wind-up; 0 = not charging. See `push()`. */
  private castMsLeft = 0;
  /** The longest remaining seen in the current wind-up — the cast bar's denominator. */
  private castSpanSeen = 1;
  /** True once `state` has changed since the last `takeStateEdge()` read — see
   * audio/README.md §6 (Hymn voice/charge cues fire on the transition, not every frame). */
  private stateChanged = false;
  /** Who the server says this entity is fixed on; 0 for nobody. Drives the arc cone. */
  targetId = 0;
  private deadAt = -1;
  private chargeT = 0;
  private highlighted = false;

  /** The interpolated transform. The only "position" a crowd body has. */
  private rx = 0;
  private ry = 0;
  private rz = 0;
  private ryaw = 0;

  /** Smoothed horizontal ground speed of the *rendered* body, m/s. Drives the clip clock. */
  private speed = 0;
  private lastX = 0;
  private lastZ = 0;
  private hasLast = false;
  /** An hp drop seen in `push()`, waiting for `animate()` to turn it into a flash. */
  private flinch = false;
  /** True once `animate()` has placed the body for real and unmuted `ambientLoop`. */
  private ambientPrimed = false;
  /** Beacon/Fabricator only: a managed `beacon_hum` loop that lives as long as this
   * instance does — see audio/README.md's ability-sounds notes. */
  private readonly ambientLoop: LoopHandle | null;

  constructor(id: number, kind: number, crowd: CrowdField | null) {
    this.id = id;
    this.kind = kind;
    this.crowd = crowd;
    this.onCrowd = !PRIMITIVE_KINDS.has(kind);
    this.def = crowdKind(kind);
    this.maxHpSeen = ENTITY_MAX_HP[kind] ?? 100;

    if (this.onCrowd) {
      this.group = null;
      this.pivot = null;
      this.shell = null;
      this.barBg = null;
      this.barFill = null;
      this.ownSlit = null;
      this.beam = null;
      this.spinner = null;
      this.barWidth = 0;
      this.barY = 0;
      this.height = this.def.height;
      this.ambientLoop = null;
      if (crowd) {
        this.slot = crowd.acquire();
        if (this.slot >= 0) {
          crowd.setLook(this.slot, this.def.look);
          crowd.setScale(this.slot, this.def.scale);
          this.writeTint(this.def.tint[0], this.def.tint[1], this.def.tint[2]);
        }
      }
      return;
    }

    const sil = buildSilhouette(kind);
    this.group = new Group();
    this.group.name = `entity:${String(id)}`;
    this.ownSlit = sil.ownSlit;
    this.beam = sil.beam;
    this.spinner = sil.spinner;
    this.height = sil.height;
    this.barWidth = sil.barWidth;
    this.barY = sil.height + 0.35;

    this.pivot = new Object3D();
    this.pivot.add(sil.root);
    this.group.add(this.pivot);

    // The outline is a back-facing copy of the same meshes: no extra geometry, no post pass,
    // and it tracks whatever silhouette this kind happens to have.
    this.shell = sil.root.clone();
    this.shell.scale.setScalar(1.06);
    this.shell.traverse((o) => {
      if (o instanceof Mesh) {
        o.material = materials().shell;
        o.castShadow = false;
        o.receiveShadow = false;
      }
    });
    this.shell.visible = false;
    this.pivot.add(this.shell);

    this.ambientLoop =
      kind === EntityKind.Beacon || kind === EntityKind.Fabricator
        ? sound.startLoop('beacon_hum', this.group.position)
        : null;
    // Built before `push()`/`apply()` ever place the group, so the panner starts at the
    // origin — silence it until the first real position lands (see `animate()`).
    this.ambientLoop?.setGain(0);

    const m = materials();
    this.barBg = new Sprite(m.barBg);
    this.barBg.scale.set(this.barWidth + 0.04, 0.11, 1);
    this.barBg.position.y = this.barY;
    this.barBg.renderOrder = 10;
    this.barBg.visible = false;
    this.barFill = new Sprite(m.barFill);
    this.barFill.scale.set(this.barWidth, 0.07, 1);
    this.barFill.position.y = this.barY;
    this.barFill.renderOrder = 11;
    this.barFill.visible = false;
    // Parented to the unrotated group: a sprite billboards its facing but not its position,
    // so a left-anchored fill hung off the yaw pivot would orbit the body.
    this.group.add(this.barBg, this.barFill);
    if (this.beam) this.group.add(this.beam);
  }

  private writeTint(r: number, g: number, b: number): void {
    if (r === this.tintR && g === this.tintG && b === this.tintB) return;
    this.tintR = r;
    this.tintG = g;
    this.tintB = b;
    if (this.crowd && this.slot >= 0) this.crowd.setTint(this.slot, r, g, b);
  }

  push(t: number, snap: EntitySnap): void {
    this.lastSeen = t;
    this.targetId = snap.targetId;
    // Level-scaled since v3: the denominator rides the wire rather than a client table.
    if (snap.maxHp > 0) this.maxHpSeen = snap.maxHp;
    this.level = snap.level;
    // The Cantor's wind-up. `castEndsAt` is on the wire **only** while the state is Charging
    // (v3 §3.1's conditional tail), so anything else is a cast that ended and the bar has to
    // go with it — a wind-up left hanging over a body is a party interrupting nothing.
    this.castMsLeft = snap.state === EntityState.Charging ? Math.max(0, snap.castEndsAt ?? 0) : 0;
    if (this.castMsLeft > this.castSpanSeen || this.castMsLeft === 0) {
      this.castSpanSeen = Math.max(1, this.castMsLeft);
    }
    if (!this.named) {
      this.named = true;
      if (this.crowd && this.slot >= 0) {
        const name = ENTITY_NAMES[this.kind] ?? `#${String(this.id)}`;
        this.crowd.setName(this.slot, snap.level > 0 ? `${name} · ${String(snap.level)}` : name);
      }
    }
    const last = this.samples.at(-1);
    if (last && t <= last.t) return; // out-of-order or duplicate
    if (snap.hp !== this.hp) {
      // An hp *drop* is the only damage signal on the wire for an entity — there is no
      // per-entity Ev.Damage the client can key off — so the flash is armed here and
      // consumed by `animate()`.
      if (snap.hp < this.hp && snap.hp > 0) this.flinch = true;
      this.hp = snap.hp;
      this.hpChangedAt = t;
    }
    this.samples.push({ t, pos: { ...snap.pos }, yaw: snap.yaw, hp: snap.hp, state: snap.state });
    if (this.samples.length > RING) this.samples.splice(0, this.samples.length - RING);
  }

  /** Places the body for the given render time (server clock, ms). */
  apply(renderTime: number, dt: number): void {
    const n = this.samples.length;
    if (n === 0) return;
    const first = this.samples[0];
    const last = this.samples.at(-1);
    if (!first || !last) return;

    if (n === 1 || renderTime <= first.t) {
      this.place(first.pos, first.yaw, first.state);
    } else if (renderTime >= last.t) {
      // Starved. EntitySnap carries no velocity, so derive one from the last pair and
      // coast on it, bounded exactly like the remote players are.
      const prev = this.samples[n - 2] ?? last;
      const span = last.t - prev.t;
      const ahead = Math.min(renderTime - last.t, MAX_EXTRAPOLATE_MS);
      const k = span > 0 ? ahead / span : 0;
      this.place(
        {
          x: last.pos.x + (last.pos.x - prev.pos.x) * k,
          y: last.pos.y + (last.pos.y - prev.pos.y) * k,
          z: last.pos.z + (last.pos.z - prev.pos.z) * k,
        },
        last.yaw,
        last.state,
      );
    } else {
      let placed = false;
      for (let i = n - 1; i > 0; i--) {
        const b = this.samples[i];
        const a = this.samples[i - 1];
        if (!a || !b) continue;
        if (a.t <= renderTime && renderTime <= b.t) {
          const span = b.t - a.t;
          const t = span > 0 ? (renderTime - a.t) / span : 0;
          this.place(
            {
              x: lerp(a.pos.x, b.pos.x, t),
              y: lerp(a.pos.y, b.pos.y, t),
              z: lerp(a.pos.z, b.pos.z, t),
            },
            lerpAngle(a.yaw, b.yaw, t),
            t < 0.5 ? a.state : b.state,
          );
          placed = true;
          break;
        }
      }
      if (!placed) this.place(last.pos, last.yaw, last.state);
    }

    this.animate(renderTime, dt);
  }

  private place(pos: Vec3, yaw: number, state: number): void {
    this.rx = pos.x;
    this.ry = pos.y;
    this.rz = pos.z;
    this.ryaw = yaw;
    if (this.group && this.pivot) {
      this.group.position.set(pos.x, pos.y, pos.z);
      this.pivot.rotation.y = yaw;
    }
    if (state !== this.state) {
      this.state = state;
      this.stateChanged = true;
    }
  }

  /** Returns the new state once, the moment `state` last changed; `null` otherwise. Reading
   * it clears the edge, so a caller polling this every frame sees each transition exactly
   * once — see `EntityManager.update()`'s Hymn voice/charge hooks. */
  takeStateEdge(): number | null {
    if (!this.stateChanged) return null;
    this.stateChanged = false;
    return this.state;
  }

  private animate(renderTime: number, dt: number): void {
    // Loot bags have no death animation — they are the death animation's leftovers.
    const dead = this.state === EntityState.Dead && this.kind !== EntityKind.LootBag;
    if (dead && this.deadAt < 0) this.deadAt = renderTime;
    if (!dead) this.deadAt = -1;

    // The rendered ground speed, differentiated here rather than in `place()`: `place()` has
    // no `dt`, and one of its three branches advances position by a *clamped* extrapolation
    // that has nothing to do with elapsed render time — reading a speed off that would drop a
    // coasting body to idle during a packet gap. Low-passed because 30 Hz snapshots sampled
    // into a 60+ fps loop otherwise flicker across the walk/run threshold.
    if (this.hasLast && dt > 0) {
      const raw = Math.min(Math.hypot(this.rx - this.lastX, this.rz - this.lastZ) / dt, SPEED_MAX);
      this.speed += (raw - this.speed) * Math.min(1, dt * 8);
      if (this.speed < SPEED_EPSILON) this.speed = 0;
    }
    this.lastX = this.rx;
    this.lastZ = this.rz;
    this.hasLast = true;

    // The 2 s rail-lance wind-up. Ramping to white over the full 2 s makes the tell readable
    // at range; the fall is ~7x faster so a cancelled charge reads instantly.
    const charging = this.state === EntityState.Charging;
    this.chargeT = charging
      ? Math.min(1, this.chargeT + dt / 2)
      : Math.max(0, this.chargeT - dt / 0.28);

    if (this.onCrowd) {
      this.animateCrowd(dead);
      return;
    }

    let sunk = 0;
    if (this.deadAt >= 0) sunk = clamp((renderTime - this.deadAt) / 1000 / SINK_SECONDS, 0, 1);
    // Sinking on the pivot rather than the group keeps the group at the authoritative
    // snapshot position, which is what hitTargets() and nearestBag() report.
    if (this.pivot) this.pivot.position.y = -SINK_DEPTH * sunk * sunk;
    if (this.group) this.group.visible = sunk < 1;
    if (this.ambientLoop && this.group) {
      this.ambientLoop.setPosition(this.group.position);
      if (!this.ambientPrimed) {
        this.ambientPrimed = true;
        this.ambientLoop.setGain(1, 300);
      }
    }

    if (this.ownSlit) {
      this.ownSlit.emissive.lerpColors(AMBER, WHITE, this.chargeT);
      this.ownSlit.emissiveIntensity = 5 + 9 * this.chargeT;
    }

    // Deployables have exactly one moving part each, which is what tells a player at a glance
    // that the thing is still alive and working.
    if (this.spinner) {
      const t = renderTime / 1000;
      switch (this.kind) {
        case EntityKind.Sentry:
          // The head sweeps unless it has a target, in which case it holds on the yaw the
          // server gave us (the pivot already carries that).
          this.spinner.rotation.y = this.targetId === 0 ? Math.sin(t * 0.9) * 0.8 : 0;
          break;
        case EntityKind.Drone:
          this.spinner.position.y = 1.5 + Math.sin(t * 2.4) * 0.12;
          this.spinner.rotation.y = t * 1.6;
          break;
        case EntityKind.Beacon: {
          const k = 0.85 + 0.25 * Math.sin(t * Math.PI * 1.4);
          this.spinner.scale.set(k, k, 1);
          this.spinner.rotation.z = t * 0.6;
          break;
        }
        case EntityKind.Fabricator:
          this.spinner.rotation.y = t * 0.8;
          break;
        default:
          break;
      }
    }

    this.updateBar(renderTime, dead, sunk);
  }

  /** The whole render step for a monster: one clip id, one speed, and a tint when it moves. */
  private animateCrowd(dead: boolean): void {
    if (this.flinch) {
      this.flinch = false;
      if (this.crowd && this.slot >= 0) this.crowd.hitFlash(this.slot);
    }
    if (!this.crowd || this.slot < 0) return;

    const anim = animForState(this.state, this.speed);
    // One walk cycle serves every speed; the death clip does not loop, so the field clamps it
    // on its last frame and the body lies where it fell until the server reaps it.
    const animSpeed = dead ? 1 : clamp(this.speed / WALK_CYCLE_SPEED, 0.6, 2.2);
    SCRATCH.x = this.rx;
    SCRATCH.y = this.ry;
    SCRATCH.z = this.rz;
    this.crowd.setState(this.slot, SCRATCH, this.ryaw, anim, animSpeed);

    // The charge tell, and the highlight, both live in the one tint channel. Charging wins:
    // a body washing to white is the two seconds the player has to move.
    const base = this.def.tint;
    const k = this.highlighted ? Math.max(this.chargeT, 0.55) : this.chargeT;
    this.writeTint(lerp(base[0], 1, k), lerp(base[1], 0.94, k), lerp(base[2], 0.86, k));
  }

  private updateBar(renderTime: number, dead: boolean, sunk: number): void {
    const bg = this.barBg;
    const fill = this.barFill;
    if (!bg || !fill) return;
    const frac = clamp(this.hp / Math.max(1, this.maxHpSeen), 0, 1);
    const recent = renderTime - this.hpChangedAt < DAMAGE_BAR_MS;
    const dx = this.rx - VIEWER.x;
    const dy = this.ry - VIEWER.y;
    const dz = this.rz - VIEWER.z;
    const near = !viewerKnown || dx * dx + dy * dy + dz * dz <= HEALTH_BAR_RANGE_SQ;
    const show =
      recent &&
      near &&
      frac < 1 &&
      !dead &&
      this.kind !== EntityKind.LootBag &&
      this.hp > 0 &&
      sunk === 0;
    bg.visible = show;
    fill.visible = show;
    if (show) {
      // Left-anchored via `center`, not `position`. A sprite billboards its facing but its
      // position is still world space, so an x offset here would swing with the camera
      // azimuth; `center` is applied inside the sprite's own screen-aligned quad. Three puts
      // the quad's left edge at -center.x * scale.x, so pinning it at -W/2 needs
      // center.x = 1 / (2 * frac). frac is never 0 — the bar hides at hp 0.
      const f = Math.max(0.001, frac);
      fill.scale.x = this.barWidth * f;
      fill.center.set(1 / (2 * f), 0.5);
    }
  }

  setHighlighted(on: boolean): void {
    if (on === this.highlighted) return;
    this.highlighted = on;
    if (this.shell) this.shell.visible = on;
    // A crowd body has no outline shell to turn on — cloning its mesh would be a per-character
    // `Object3D`, which is the one thing this rewrite exists to stop. It brightens instead,
    // written on the next `animateCrowd`.
  }

  /**
   * Choir Anchor only: point the light shaft at the Verger's chest. `null` (no boss in the
   * snapshot, or the anchor is down) hides it — an anchor tethered to nothing is a lie about
   * whether the Choir Shield is still up.
   *
   * The beam is parented to the *unrotated* group, so its local frame is the world frame
   * translated by the anchor's position and no yaw has to be undone.
   */
  aimBeam(target: Vector3 | null): void {
    const beam = this.beam;
    if (!beam) return;
    if (!target || this.isDead) {
      beam.visible = false;
      return;
    }
    const dx = target.x - this.rx;
    const dy = target.y - (this.ry + ANCHOR_TOP_Y);
    const dz = target.z - this.rz;
    const len = Math.hypot(dx, dy, dz);
    if (len < 0.3) {
      beam.visible = false;
      return;
    }
    beam.visible = true;
    beam.position.set(dx * 0.5, ANCHOR_TOP_Y + dy * 0.5, dz * 0.5);
    beam.scale.set(1, len, 1);
    // The cylinder runs along +Y, so rotate that axis onto the direction of travel.
    BEAM_DIR.set(dx / len, dy / len, dz / len);
    beam.quaternion.setFromUnitVectors(BEAM_UP, BEAM_DIR);
  }

  get isDead(): boolean {
    return this.state === EntityState.Dead;
  }

  get currentHp(): number {
    return this.hp;
  }

  get currentMaxHp(): number {
    return this.maxHpSeen;
  }

  get currentLevel(): number {
    return this.level;
  }

  get currentState(): number {
    return this.state;
  }

  /** Milliseconds left on the wind-up, straight off the snapshot tail. 0 = not charging. */
  get castMs(): number {
    return this.castMsLeft;
  }

  /** 0 as the wind-up starts, 1 as it lands. The target frame's bar. */
  get castFraction(): number {
    if (this.castMsLeft <= 0) return 0;
    return Math.max(0, Math.min(1, 1 - this.castMsLeft / (this.castSpanSeen || 1)));
  }

  /** The crowd slot this body draws on, or -1. Only the nameplate hooks want it. */
  get plateSlot(): number {
    return this.slot;
  }

  /** Rendered yaw, which is the interpolated one — not the last snapshot's. */
  get renderYaw(): number {
    return this.ryaw;
  }

  get renderX(): number {
    return this.rx;
  }
  get renderY(): number {
    return this.ry;
  }
  get renderZ(): number {
    return this.rz;
  }

  hitTarget(out: HitTarget): HitTarget {
    out.id = this.id;
    out.pos.x = this.rx;
    out.pos.y = this.ry;
    out.pos.z = this.rz;
    out.crouching = false;
    out.height = this.height;
    return out;
  }

  dispose(): void {
    // Only the cloned Verger slit is instance-owned; everything else is shared.
    this.ownSlit?.dispose();
    this.ambientLoop?.stop(300);
    // Scale *and* tint restored: a Chorister released at its ash tint would hand the next
    // acquirer — very possibly a player — a near-black body. See `releaseSlot`.
    if (this.crowd) releaseSlot(this.crowd, this.slot);
    this.slot = -1;
  }
}

/**
 * DevTools handle: `__stillsunHymns` reports how many entities are in the snapshot and how
 * many of them are drawn as crowd instances. Read by `tools/scripts/perf-probe.mjs`.
 * `animated`, `shadowed` and `clips` are pinned — there are no mixers and no skinned shadow
 * casters left — and are kept only so the probe's field reads do not go undefined.
 */
const hymnDebug: {
  total: number;
  rigged: number;
  animated: number;
  shadowed: number;
  clips: string[];
} = { total: 0, rigged: 0, animated: 0, shadowed: 0, clips: [] };
(globalThis as { __stillsunHymns?: unknown }).__stillsunHymns = hymnDebug;

export class EntityManager {
  private readonly entities = new Map<number, RenderedEntity>();
  private readonly scene: Scene;
  private readonly crowd: CrowdField | null;
  private readonly targets: HitTarget[] = [];
  private readonly attackScratch: Attacker[] = [];
  /** id -> kind for entities we have seen, kept after they are reaped (see nameOf). */
  private readonly knownKinds = new Map<number, number>();
  private highlightId = 0;
  /** Seconds since construction; drives every cosmetic pulse. */
  private clock = 0;

  /**
   * `crowd` is nullable for the same reason `RemoteManager`'s is: main.ts warns and carries on
   * when the bake is unreachable. Furniture still draws; monsters simply have no body.
   */
  constructor(scene: Scene, crowd: CrowdField | null) {
    this.scene = scene;
    this.crowd = crowd;
  }

  get count(): number {
    return this.entities.size;
  }

  /**
   * Kept because main.ts awaits it before `precompileScene`. There is nothing left to warm:
   * `CrowdField.create()` builds every body and gear mesh up front, before the same precompile,
   * precisely so no character pipeline is ever compiled on screen (`docs/WEBGPU-NOTES.md` §1).
   */
  async warmRigs(): Promise<void> {
    await Promise.resolve();
  }

  onSnapshot(serverTime: number, snaps: readonly EntitySnap[]): void {
    for (const s of snaps) {
      let e = this.entities.get(s.id);
      if (!e) {
        this.knownKinds.set(s.id, s.kind);
        // A city spawns tens of monsters, not thousands; a hard cap keeps a long session from
        // growing the map without bound if the server ever recycles ids.
        if (this.knownKinds.size > 512) {
          const oldest = this.knownKinds.keys().next();
          if (!oldest.done) this.knownKinds.delete(oldest.value);
        }
        e = new RenderedEntity(s.id, s.kind, this.crowd);
        this.entities.set(s.id, e);
        if (e.group) this.scene.add(e.group);
        if (this.highlightId === s.id) e.setHighlighted(true);
      }
      e.push(serverTime, s);
    }
  }

  /**
   * `viewer` is the camera position, handed in rather than found. The old `locateViewer()`
   * scanned `scene.children` for an `isCamera` object every frame to spare main.ts one
   * argument; at city scale that is a walk of the top-level scene list sixty times a second
   * for a number the caller already has.
   */
  update(serverNow: number, dt: number, viewer: Vec3): void {
    this.clock += dt;
    VIEWER.x = viewer.x;
    VIEWER.y = viewer.y;
    VIEWER.z = viewer.z;
    viewerKnown = true;

    const renderTime = serverNow - INTERP_DELAY_MS;
    const m = materials();
    // Shared pulses, evaluated once per frame rather than once per instance.
    m.shell.emissiveIntensity = 3 + 2 * Math.sin(this.clock * Math.PI * 3); // 1.5 Hz
    m.bag.emissiveIntensity = 3.25 + 1.75 * Math.sin(this.clock * Math.PI * 1.6); // 0.8 Hz

    let onCrowd = 0;
    for (const [id, e] of this.entities) {
      // Absence is measured on the server clock, the same one the snapshots stamp.
      if (serverNow - e.lastSeen > STALE_MS) {
        if (e.group) this.scene.remove(e.group);
        e.dispose();
        this.entities.delete(id);
        if (this.highlightId === id) this.highlightId = 0;
        continue;
      }
      e.apply(renderTime, dt);
      if (e.group === null) onCrowd += 1;

      const edge = e.takeStateEdge();
      if (edge !== null) {
        SCRATCH.x = e.renderX;
        SCRATCH.y = e.renderY;
        SCRATCH.z = e.renderZ;
        if (
          e.kind === EntityKind.Chorister &&
          (edge === EntityState.Alert || edge === EntityState.Attack)
        ) {
          sound.hymnVoice('chorister', SCRATCH);
        } else if (e.kind === EntityKind.Cantor && edge === EntityState.Charging) {
          // 2000 ms matches the rail-lance wind-up this same edge drives in `animate()`.
          sound.cantorCharge(SCRATCH, 2000);
        }
      }
      // Cheapest call site for the drone heartbeat: once per live, non-stale Sexton.
      if (e.kind === EntityKind.Sexton && !e.isDead) {
        SCRATCH.x = e.renderX;
        SCRATCH.y = e.renderY;
        SCRATCH.z = e.renderZ;
        sound.sextonDrone(id, SCRATCH);
      }
    }

    hymnDebug.total = this.entities.size;
    hymnDebug.rigged = onCrowd;

    // The Choir Deck reads as one machine or as scenery: every live anchor keeps a white
    // shaft on the Verger, and they all go out together when it dies.
    const boss = this.bossEntity();
    let target: Vector3 | null = null;
    if (boss && !boss.isDead) {
      target = BOSS_POS.set(boss.renderX, boss.renderY + VERGER_CHEST_Y, boss.renderZ);
    }
    for (const e of this.entities.values()) {
      if (e.kind === EntityKind.ChoirAnchor) e.aimBeam(target);
    }
  }

  private bossEntity(): RenderedEntity | null {
    for (const e of this.entities.values()) if (e.kind === EntityKind.Verger) return e;
    return null;
  }

  /** The Verger's health for the HUD boss bar, or `null` when none is in the snapshot. */
  boss(): { name: string; hp: number; maxHp: number } | null {
    const e = this.bossEntity();
    if (!e || e.isDead) return null;
    return {
      name: ENTITY_NAMES[EntityKind.Verger] ?? 'The Verger',
      hp: e.currentHp,
      // Level-scaled since v3: the wire's `maxHp`, not a client table.
      maxHp: e.currentMaxHp,
    };
  }

  /**
   * Everything the target frame needs about one Hymn, or `null` when it is not (or no longer)
   * in the snapshot. `castMs` is the wind-up (`EntitySnap.castEndsAt`); a dead entity still
   * answers, so the frame can show a corpse at zero rather than blinking out mid-fight.
   */
  targetInfo(id: number): {
    name: string;
    level: number;
    hp: number;
    maxHp: number;
    castMs: number;
    castFraction: number;
  } | null {
    const e = this.entities.get(id);
    if (!e) return null;
    return {
      name: ENTITY_NAMES[e.kind] ?? `#${String(id)}`,
      level: e.currentLevel,
      hp: e.currentHp,
      maxHp: e.currentMaxHp,
      castMs: e.castMs,
      castFraction: e.castFraction,
    };
  }

  /**
   * Put a health bar on one entity's nameplate — the selected target's, and nobody else's.
   *
   * Returns false when this id is not an entity of ours (or has no body to hang a plate on),
   * which is the caller's cue to try the other side of the world rather than clearing: the
   * crowd field keeps exactly one bar, so two writers taking turns every frame would flicker
   * it. Cheap enough to call every frame — nothing here touches the DOM.
   */
  setTargetPlate(id: number): boolean {
    if (!this.crowd || id === 0) return false;
    const e = this.entities.get(id);
    if (!e || e.plateSlot < 0) return false;
    const max = Math.max(1, e.currentMaxHp);
    this.crowd.setPlateHp(e.plateSlot, Math.max(0, Math.min(1, e.currentHp / max)));
    return true;
  }

  /** Live positions for a reveal, and for resolving an ability leash endpoint. */
  positionOf(id: number): Vec3 | null {
    const e = this.entities.get(id);
    if (!e || e.isDead) return null;
    return { x: e.renderX, y: e.renderY, z: e.renderZ };
  }

  /** Everything a reveal should paint: live Hymns, not our own deployables. */
  revealPoints(out: { pos: Vec3; height: number; friendly: boolean }[]): number {
    let n = 0;
    for (const e of this.entities.values()) {
      if (e.isDead || !HYMN_KINDS.has(e.kind)) continue;
      const slot = out[n];
      if (slot) {
        slot.pos.x = e.renderX;
        slot.pos.y = e.renderY;
        slot.pos.z = e.renderZ;
        slot.height = e.height;
        slot.friendly = false;
      } else {
        out.push({
          pos: { x: e.renderX, y: e.renderY, z: e.renderZ },
          height: e.height,
          friendly: false,
        });
      }
      n += 1;
    }
    return n;
  }

  /**
   * A kill feed entry is written the moment a monster dies, and the entity itself is reaped a
   * few seconds later — but the *feed* may be rebuilt after that, and a Death event can name
   * an entity we have already dropped. So kinds outlive their entities.
   */
  nameOf(id: number): string {
    const kind = this.entities.get(id)?.kind ?? this.knownKinds.get(id);
    if (kind === undefined) return `#${String(id)}`;
    return ENTITY_NAMES[kind] ?? `#${String(id)}`;
  }

  kindOf(id: number): number {
    return this.entities.get(id)?.kind ?? this.knownKinds.get(id) ?? 0;
  }

  /**
   * Entities of one kind currently in `EntityState.Attack`. The Sexton's arc thrower is the
   * reason this exists: it deals damage without ever emitting an event, so the only way to
   * draw it is to notice the state and the facing ourselves.
   */
  attackers(kind: number): readonly Attacker[] {
    let n = 0;
    for (const e of this.entities.values()) {
      if (e.kind !== kind || e.isDead) continue;
      if (e.currentState !== EntityState.Attack) continue;
      let slot = this.attackScratch[n];
      if (!slot) {
        slot = { id: 0, kind: 0, pos: { x: 0, y: 0, z: 0 }, yaw: 0, targetId: 0 };
        this.attackScratch.push(slot);
      }
      slot.id = e.id;
      slot.kind = e.kind;
      slot.pos.x = e.renderX;
      slot.pos.y = e.renderY;
      slot.pos.z = e.renderZ;
      slot.yaw = e.renderYaw;
      slot.targetId = e.targetId;
      n += 1;
    }
    return n === this.attackScratch.length ? this.attackScratch : this.attackScratch.slice(0, n);
  }

  /**
   * Live entities at their currently *rendered* positions, for the click-to-target ray. Loot
   * bags and drones are not selectable. Reuses `this.targets` — no per-frame garbage.
   */
  hitTargets(): readonly HitTarget[] {
    let n = 0;
    for (const e of this.entities.values()) {
      // Deployables are friendly furniture: a ray must pass a Bulwark's edge rather than stop
      // dead on a beacon the player put there themselves.
      if (e.isDead || NON_TARGET_KINDS.has(e.kind)) continue;
      let slot = this.targets[n];
      if (!slot) {
        slot = { id: 0, pos: { x: 0, y: 0, z: 0 }, crouching: false };
        this.targets.push(slot);
      }
      e.hitTarget(slot);
      n += 1;
    }
    return n === this.targets.length ? this.targets : this.targets.slice(0, n);
  }

  nearestBag(from: Vec3, maxDist: number): NearEntity | null {
    let best: NearEntity | null = null;
    for (const e of this.entities.values()) {
      if (e.kind !== EntityKind.LootBag) continue;
      const d = Math.hypot(e.renderX - from.x, e.renderY - from.y, e.renderZ - from.z);
      if (d > maxDist) continue;
      if (best && d >= best.dist) continue;
      best = { id: e.id, kind: e.kind, dist: d, pos: { x: e.renderX, y: e.renderY, z: e.renderZ } };
    }
    return best;
  }

  setHighlight(id: number): void {
    if (id === this.highlightId) return;
    this.entities.get(this.highlightId)?.setHighlighted(false);
    this.highlightId = id;
    this.entities.get(id)?.setHighlighted(true);
  }

  clear(): void {
    for (const e of this.entities.values()) {
      if (e.group) this.scene.remove(e.group);
      e.dispose();
    }
    this.entities.clear();
    this.knownKinds.clear();
    this.highlightId = 0;
  }
}
